import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { StarRating } from "@/components/StarRating";
import VerifiedBadge from "@/components/VerifiedBadge";
import ProBadge from "@/components/ProBadge";
import FoundingMemberBadge from "@/components/FoundingMemberBadge";
import { Search, MapPin, Loader2, LocateFixed, XCircle, ArrowRight } from "lucide-react";
import { TAXONOMY } from "@/data/taxonomy";
import { CATEGORY_IMAGES, FALLBACK_CATEGORY_IMAGE } from "@/data/categoryImages";
import BusinessListingCard from "@/components/BusinessListingCard";
import TrendingNearYouCarousel from "@/components/TrendingNearYouCarousel";
import PopularNowRail from "@/components/PopularNowRail";
import FriendsFavoritedCarousel from "@/components/FriendsFavoritedCarousel";

const BUSINESSES_PAGE_SIZE = 24;

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("businesses");
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [zip, setZip] = useState(params.get("zip") || "");
  const [cuisine, setCuisine] = useState(params.get("cuisine") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [sortBy, setSortBy] = useState(params.get("sort") || "popular");
  const [coords, setCoords] = useState(null); // {lat, lng} when "Near me" is on
  const [radiusMi, setRadiusMi] = useState(25);
  const [locating, setLocating] = useState(false);
  const [locateErr, setLocateErr] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");
  const loadIdRef = useRef(0);
  const sentinelRef = useRef(null);

  const buildParams = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (city) p.city = city;
    if (state) p.state = state;
    if (zip) p.zip = zip;
    if (cuisine) p.cuisine = cuisine;
    if (category && category !== "all") p.category = category;
    if (sortBy && sortBy !== "popular") p.sort = sortBy;
    if (coords) {
      p.lat = coords.lat;
      p.lng = coords.lng;
      p.radius_km = +(radiusMi * 1.60934).toFixed(2);
    }
    return p;
  }, [q, city, state, zip, cuisine, category, sortBy, coords, radiusMi]);

  const requestLocation = () => {
    setLocateErr("");
    if (!navigator.geolocation) {
      setLocateErr("Your browser doesn't support geolocation.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocateErr(
          err.code === err.PERMISSION_DENIED
            ? "Location blocked — enable it in your browser to use Near me."
            : "Couldn't get your location. Try again."
        );
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 }
    );
  };

  const clearNearMe = () => { setCoords(null); setLocateErr(""); };

  // Initial / filter-change load
  useEffect(() => {
    setLoading(true);
    setErr("");
    setItems([]);
    setOffset(0);
    setHasMore(false);
    const myLoadId = ++loadIdRef.current;
    const p = buildParams();
    if (tab === "businesses") {
      api.get("/businesses", { params: { ...p, offset: 0, limit: BUSINESSES_PAGE_SIZE } })
        .then(({ data }) => {
          if (loadIdRef.current !== myLoadId) return;
          setItems(data.items || []);
          setHasMore(!!data.has_more);
          setOffset((data.items || []).length);
          setTotal(data.total || 0);
        })
        .catch(() => {
          if (loadIdRef.current !== myLoadId) return;
          setItems([]);
        })
        .finally(() => {
          if (loadIdRef.current === myLoadId) setLoading(false);
        });
    } else {
      api.get("/customers/directory", { params: p })
        .then(({ data }) => {
          if (loadIdRef.current !== myLoadId) return;
          setItems(Array.isArray(data) ? data : []);
          setHasMore(false);
        })
        .catch((e) => {
          if (loadIdRef.current !== myLoadId) return;
          setItems([]);
          const code = e.response?.status;
          if (code === 401) setErr("Log in as a business with an active subscription to browse customers.");
          else if (code === 402) setErr("Active business subscription required to view the customer directory.");
          else setErr("Failed to load customers.");
        })
        .finally(() => {
          if (loadIdRef.current === myLoadId) setLoading(false);
        });
    }
    // Sync URL
    const newParams = {};
    if (q) newParams.q = q;
    if (city) newParams.city = city;
    if (state) newParams.state = state;
    if (category && category !== "all") newParams.category = category;
    setParams(newParams, { replace: true });
  }, [tab, q, city, state, category, sortBy, coords, radiusMi]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore || tab !== "businesses") return;
    setLoadingMore(true);
    const myLoadId = loadIdRef.current;
    try {
      const p = buildParams();
      const { data } = await api.get("/businesses", { params: { ...p, offset, limit: BUSINESSES_PAGE_SIZE } });
      if (loadIdRef.current !== myLoadId) return;
      const newItems = data.items || [];
      setItems((prev) => [...prev, ...newItems]);
      setHasMore(!!data.has_more);
      setOffset((o) => o + newItems.length);
    } catch (e) {
      console.warn("loadMore failed:", e);
    } finally {
      if (loadIdRef.current === myLoadId) setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, tab, offset, buildParams]);

  // IntersectionObserver — triggers loadMore when sentinel scrolls into view
  useEffect(() => {
    if (tab !== "businesses") return;
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "240px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [tab, loadMore, hasMore, items.length]);


  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="overline text-gray-500">Public directory</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Browse the ledger</h1>
      <p className="mt-2 text-gray-600 max-w-lg">Ranked by popularity &amp; recent activity — reviews, check-ins, photos and live offers all count. Filter by category, city, or state.</p>

      {/* All main categories showcase — mirrors the Landing "Popular categories"
          grid but renders every top-level group in TAXONOMY (not just the 12
          curated ones). Clicking a tile filters the directory below. Hidden
          once the user has picked a category or typed a search, so it doesn't
          crowd the result set. */}
      {tab === "businesses" && category === "all" && !q.trim() && !zip.trim() && !cuisine.trim() && (
        <section className="mt-8" data-testid="directory-all-categories">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="overline text-[#B31E1E]">Browse by category</div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">All main categories</h2>
            </div>
            <Link
              to="/categories"
              className="text-xs text-[#B31E1E] font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
              data-testid="directory-see-subcategories"
            >
              Browse subcategories <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="directory-categories-grid">
            {TAXONOMY.map((t) => {
              const img = CATEGORY_IMAGES[t.group] || FALLBACK_CATEGORY_IMAGE;
              const count = t.items?.length || 0;
              const slug = t.group.replace(/\s+/g, "-").toLowerCase();
              return (
                <Link
                  key={t.group}
                  to={`/directory?category=${encodeURIComponent(t.group)}`}
                  onClick={(e) => {
                    // Filter in-place instead of full navigation — smoother UX.
                    e.preventDefault();
                    setCategory(t.group);
                    setItems([]);
                    setOffset(0);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#FAF5F2] transition-transform duration-300 hover:-translate-y-1"
                  data-testid={`directory-category-tile-${slug}`}
                >
                  <img
                    src={img}
                    alt={t.group}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                  <div className="absolute top-2.5 right-2.5 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-mono font-semibold text-[#1B1614] backdrop-blur-sm">
                    {count}+ services
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <div className="text-white font-black text-[15px] leading-tight tracking-tight drop-shadow-lg">
                      {t.group}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-white/85 opacity-0 group-hover:opacity-100 transition-opacity">
                      Browse <ArrowRight size={10} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setItems([]); }} className="mt-8">
        <TabsList className="rounded-sm">
          <TabsTrigger value="businesses" className="rounded-sm" data-testid="directory-tab-businesses">Businesses</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="directory-tab-customers">Customers</TabsTrigger>
        </TabsList>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder={tab === "businesses" ? "Search name, address, category" : "Search name"} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm pl-9" data-testid="directory-search-input" />
          </div>
          {tab === "businesses" && (
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-sm" data-testid="directory-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent className="max-h-96">
                <SelectItem value="all">All categories</SelectItem>
                {TAXONOMY.map((t) => (
                  <SelectGroup key={t.group}>
                    <SelectLabel>{t.group}</SelectLabel>
                    <SelectItem value={t.group}>All {t.group}</SelectItem>
                    {t.items.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-sm" data-testid="directory-city-input" />
          <Input placeholder="State" value={state} onChange={(e) => setState(e.target.value)} className="rounded-sm" data-testid="directory-state-input" />
        </div>

        {/* Extra restaurant-friendly filters — ZIP and cuisine keyword.
            Cuisine matches on the business's `cuisine` field, or category /
            categories arrays so "Italian" hits "Italian Restaurants" too. */}
        {tab === "businesses" && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="directory-cuisine-row">
            <Input
              placeholder="ZIP code"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
              className="rounded-sm font-mono"
              data-testid="directory-zip-input"
              inputMode="numeric"
            />
            <Input
              placeholder="Cuisine or specialty (e.g. Italian, Tacos, BBQ)"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
              className="rounded-sm"
              data-testid="directory-cuisine-input"
            />
          </div>
        )}

        {tab === "businesses" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="directory-sort-row">
            <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Sort</span>
            {[
              { v: "popular", label: "Most popular", hint: "Composite of reviews, check-ins, photos & offers (last 30d weighted)" },
              { v: "rating", label: "Highest rated", hint: "Rating average, ties broken by review count" },
              { v: "newest", label: "Newest", hint: "Recently listed businesses first" },
            ].map((o) => {
              const active = sortBy === o.v && !coords;
              const disabled = !!coords; // near_me forces distance sort
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => { if (!disabled) setSortBy(o.v); }}
                  disabled={disabled}
                  title={disabled ? "Near-me is on — sorted by distance" : o.hint}
                  className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border transition-colors ${
                    active
                      ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                      : "bg-white text-gray-700 border-border hover:bg-gray-50"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid={`directory-sort-${o.v}`}
                >
                  {o.label}
                </button>
              );
            })}
            {coords && (
              <span className="text-[11px] font-mono text-gray-500 italic" data-testid="directory-sort-locked-hint">
                Sorted by distance — clear "Near me" to change
              </span>
            )}
          </div>
        )}

        {tab === "businesses" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="directory-near-me-row">
            {!coords ? (
              <button
                type="button"
                onClick={requestLocation}
                disabled={locating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[35px] text-xs font-mono border border-[#B31E1E]/30 text-[#B31E1E] bg-[#B31E1E]/5 hover:bg-[#B31E1E]/10 shadow-sm hover:shadow-md disabled:opacity-50"
                data-testid="directory-near-me-button"
              >
                {locating ? <Loader2 size={13} className="animate-spin" /> : <LocateFixed size={13} />}
                {locating ? "Locating…" : "Locations near me"}
              </button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[35px] text-xs font-mono bg-[#B31E1E] text-white" data-testid="directory-near-me-active">
                  <LocateFixed size={13} /> Near me — within {radiusMi} mi
                </span>
                <div className="flex gap-1">
                  {[5, 10, 25, 50, 100].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setRadiusMi(m)}
                      className={`px-2.5 py-1 rounded-[35px] text-[11px] font-mono border ${
                        radiusMi === m
                          ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                          : "bg-white text-gray-700 border-border hover:bg-gray-50"
                      }`}
                      data-testid={`directory-radius-${m}`}
                    >
                      {m} mi
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={clearNearMe}
                  className="inline-flex items-center gap-1 text-xs font-mono text-gray-500 hover:text-rose-600"
                  data-testid="directory-near-me-clear"
                >
                  <XCircle size={13} /> Clear
                </button>
              </>
            )}
            {locateErr && (
              <span className="text-xs text-rose-600 font-mono" data-testid="directory-near-me-error">{locateErr}</span>
            )}
          </div>
        )}

        <TabsContent value="businesses">
          {/* Friend-graph discovery — only shows for signed-in customers with ≥1 friend-fav in last 30d */}
          <div className="max-w-4xl">
            <FriendsFavoritedCarousel />
          </div>
          {/* Time-of-day-aware "Popular now" — coffee before noon, dinner after 5, etc. */}
          <div className="mt-8 max-w-4xl">
            <PopularNowRail />
          </div>
          {/* Trending rail — only renders when there are trending listings; silent otherwise */}
          <div className="max-w-4xl">
            <TrendingNearYouCarousel />
          </div>
          <div className="flex flex-col gap-5 max-w-4xl" data-testid="directory-list-businesses">
            {loading && <p className="text-gray-500">Loading...</p>}
            {!loading && items.length === 0 && <p className="text-gray-500">No businesses match your filters.</p>}
            {items.map((b, idx) => (
              <BusinessListingCard key={b.id} business={b} index={idx} />
            ))}
          </div>
          {tab === "businesses" && !loading && (items.length > 0 || hasMore) && (
            <div ref={sentinelRef} className="mt-10 flex flex-col items-center text-xs font-mono text-gray-500" data-testid="directory-sentinel">
              {loadingMore && (
                <div className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading more…
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <span data-testid="directory-end-of-list">
                  Showing all {total} listing{total === 1 ? "" : "s"}.
                </span>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="customers">
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-list-customers">
            {loading && <p className="text-gray-500">Loading...</p>}
            {!loading && err && <p className="text-amber-700" data-testid="directory-customers-gated">{err}</p>}
            {!loading && !err && items.length === 0 && <p className="text-gray-500">No customers found.</p>}
            {items.map((c, idx) => (
              <Card key={c.id} className={`rounded-sm border border-border p-6 fade-up-${(idx % 4) + 1}`} data-testid={`directory-customer-${c.id}`}>
                <h3 className="font-display text-xl">{c.name}</h3>
                <p className="mt-2 text-sm text-gray-600">{[c.city, c.state].filter(Boolean).join(", ") || "Unknown"}</p>
                <div className="mt-4 flex items-center gap-2">
                  <StarRating value={c.rating_avg} />
                  <span className="text-sm text-gray-500">{(c.rating_avg || 0).toFixed(1)} · {c.rating_count} reviews</span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
